import React, { useMemo } from 'react';
import { Sparkles, AlertTriangle, ArrowUpRight, ArrowDownRight, Star } from 'lucide-react';
import { PERSONALITY_TRAITS } from '../../data/personalityTraits';
import { analyzeDriverCompatibility } from '../../utils/compatibility';

const formatDelta = (delta) => `${delta > 0 ? '+' : ''}${delta.toFixed(2)}/j`;

export function TeamCompatibilityMatrix({ drivers, vehicles, activeContracts }) {
  const rows = useMemo(() => {
    return (drivers || [])
      .map(d => ({ driver: d, analysis: analyzeDriverCompatibility(d, vehicles, activeContracts) }))
      .filter(r => r.analysis.hasImpactingTraits)
      .sort((a, b) => {
        // Sort: critical projections first, then worst current delta, then alpha.
        const aDays = a.analysis.daysToCritical;
        const bDays = b.analysis.daysToCritical;
        const aCrit = aDays !== null && aDays !== Infinity ? aDays : 9999;
        const bCrit = bDays !== null && bDays !== Infinity ? bDays : 9999;
        if (aCrit !== bCrit) return aCrit - bCrit;
        if (a.analysis.currentDelta !== b.analysis.currentDelta) return a.analysis.currentDelta - b.analysis.currentDelta;
        return a.driver.name.localeCompare(b.driver.name);
      });
  }, [drivers, vehicles, activeContracts]);

  if (rows.length === 0) {
    return (
      <div className="surface-card p-4" data-testid="compat-empty">
        <p className="text-sm text-slate-500 italic">
          Aucun chauffeur avec des traits impactants pour le moment. Embauche des profils marqués (Old school, Ambitieux, Casanier…) pour activer la matrice.
        </p>
      </div>
    );
  }

  return (
    <div className="surface-card overflow-hidden" data-testid="compat-matrix">
      <div className="px-4 py-3 border-b border-ink-600 flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-accent" />
        <h3 className="font-display text-sm font-bold text-foreground tracking-tight uppercase">
          Compatibilité équipe × missions
        </h3>
        <span className="font-mono text-[10px] text-slate-500 ml-auto">
          {rows.length} chauffeur(s) analysé(s) · projection sat. critique &lt; 30
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-slate-500 border-b border-ink-600">
            <tr className="text-left">
              <th className="px-3 py-2 font-mono uppercase tracking-wider text-[10px]">Chauffeur</th>
              <th className="px-3 py-2 font-mono uppercase tracking-wider text-[10px]">Traits</th>
              <th className="px-3 py-2 font-mono uppercase tracking-wider text-[10px]">Aime</th>
              <th className="px-3 py-2 font-mono uppercase tracking-wider text-[10px]">Déteste</th>
              <th className="px-3 py-2 font-mono uppercase tracking-wider text-[10px]">Préf. véhicule</th>
              <th className="px-3 py-2 font-mono uppercase tracking-wider text-[10px] text-right">Mission actuelle</th>
              <th className="px-3 py-2 font-mono uppercase tracking-wider text-[10px] text-right">ROI</th>
              <th className="px-3 py-2 font-mono uppercase tracking-wider text-[10px] text-right">Projection</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ driver, analysis }) => {
              const critWarn = analysis.daysToCritical !== null
                && analysis.daysToCritical !== Infinity
                && analysis.daysToCritical <= 30;
              return (
                <tr
                  key={driver.id}
                  className={`border-t border-ink-700 ${critWarn ? 'bg-danger/5' : ''}`}
                  data-testid={`compat-row-${driver.id}`}
                >
                  <td className="px-3 py-2.5">
                    <p className="font-semibold text-slate-100">{driver.name}</p>
                    <p className="text-[10px] text-slate-500">
                      Sat. {Math.round(driver.satisfaction || 0)}/100 · Niv. {driver.level}
                    </p>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex flex-wrap gap-1">
                      {(driver.personalityTraits || []).map(traitId => {
                        const td = PERSONALITY_TRAITS[traitId];
                        if (!td) return null;
                        return (
                          <span
                            key={traitId}
                            className="text-[10px] px-1.5 py-0.5 rounded-full bg-accent/10 text-accent font-medium border border-accent/30"
                            title={td.description}
                          >
                            {td.label}
                          </span>
                        );
                      })}
                    </div>
                  </td>
                  <td className="px-3 py-2.5">
                    {analysis.bestContracts.length === 0 ? (
                      <span className="text-[10px] text-slate-500 italic">—</span>
                    ) : (
                      <div className="space-y-0.5">
                        {analysis.bestContracts.map(c => (
                          <p key={c.category} className="text-[10px] text-money flex items-center gap-1">
                            <ArrowUpRight className="w-2.5 h-2.5" />
                            {c.category} <span className="font-mono opacity-80">{formatDelta(c.delta)}</span>
                          </p>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    {analysis.worstContracts.length === 0 ? (
                      <span className="text-[10px] text-slate-500 italic">—</span>
                    ) : (
                      <div className="space-y-0.5">
                        {analysis.worstContracts.map(c => (
                          <p key={c.category} className="text-[10px] text-danger flex items-center gap-1">
                            <ArrowDownRight className="w-2.5 h-2.5" />
                            {c.category} <span className="font-mono opacity-80">{formatDelta(c.delta)}</span>
                          </p>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    {analysis.bestTier ? (
                      <p className="text-[10px] text-money flex items-center gap-1">
                        <Star className="w-2.5 h-2.5" />
                        {analysis.bestTier.label}
                      </p>
                    ) : (
                      <span className="text-[10px] text-slate-500 italic">—</span>
                    )}
                    {analysis.worstTier && (
                      <p className="text-[10px] text-danger mt-0.5 flex items-center gap-1">
                        <ArrowDownRight className="w-2.5 h-2.5" />
                        Évite {analysis.worstTier.label}
                      </p>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    {driver.status !== 'active' ? (
                      <span className="text-[10px] text-slate-500 italic">
                        {driver.status === 'idle' ? 'Disponible' :
                         driver.status === 'training' ? 'En formation' :
                         driver.status === 'vacation' ? 'En congés' :
                         driver.status === 'sick' ? 'Arrêt maladie' : driver.status}
                      </span>
                    ) : analysis.currentDelta === 0 ? (
                      <span className="text-[10px] text-slate-400 font-mono">neutre</span>
                    ) : (
                      <span className={`inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full font-mono font-bold border ${
                        analysis.currentDelta > 0 ? 'bg-money/10 text-money border-money/40' : 'bg-danger/10 text-danger border-danger/40'
                      }`} data-testid={`compat-current-${driver.id}`}>
                        {analysis.currentDelta > 0 ? <ArrowUpRight className="w-2.5 h-2.5" /> : <ArrowDownRight className="w-2.5 h-2.5" />}
                        {formatDelta(analysis.currentDelta)}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-right" data-testid={`compat-roi-${driver.id}`}>
                    {analysis.personalityROI === null ? (
                      <span className="text-[10px] text-slate-500 italic">—</span>
                    ) : (() => {
                      const roi = analysis.personalityROI;
                      const tone = roi >= 80 ? 'text-money'
                                  : roi >= 50 ? 'text-warn'
                                  : 'text-danger';
                      const recommendation = analysis.bestCombo && analysis.bestCombo.delta > analysis.currentDelta + 0.05
                        ? `Mieux : ${analysis.bestCombo.category}${analysis.bestCombo.engine === 'electric' ? ' · électrique' : ''} → ${analysis.bestCombo.delta > 0 ? '+' : ''}${analysis.bestCombo.delta.toFixed(2)}/j`
                        : null;
                      return (
                        <div>
                          <p className={`font-mono font-bold text-xs ${tone}`}>{roi}%</p>
                          <p className="text-[10px] text-slate-500 font-mono">
                            {formatDelta(Math.max(0, analysis.currentDelta))} / max {analysis.maxPossibleDelta.toFixed(2)}/j
                          </p>
                          {recommendation && (
                            <p className="text-[10px] text-accent mt-0.5 leading-tight" data-testid={`compat-reco-${driver.id}`}>
                              {recommendation}
                            </p>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    {analysis.daysToCritical === null ? (
                      <span className="text-[10px] text-slate-500 italic">—</span>
                    ) : analysis.daysToCritical === Infinity ? (
                      <span className="text-[10px] text-money font-mono">stable</span>
                    ) : analysis.daysToCritical === 0 ? (
                      <span className="text-[10px] text-danger font-mono font-bold flex items-center justify-end gap-1" data-testid={`compat-crit-${driver.id}`}>
                        <AlertTriangle className="w-3 h-3" />
                        Déjà critique
                      </span>
                    ) : (
                      <span className={`text-[10px] font-mono font-bold ${critWarn ? 'text-danger' : 'text-warn'}`} data-testid={`compat-proj-${driver.id}`}>
                        sat. &lt; 30 dans {analysis.daysToCritical}j
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="px-4 py-2 border-t border-ink-600 bg-ink-900/50">
        <p className="text-[10px] text-slate-500 italic leading-snug">
          💡 <span className="text-money">ROI ≥ 80 %</span> = chauffeur exploité au mieux · <span className="text-warn">50–79 %</span> = ajustement utile · <span className="text-danger">&lt; 50 %</span> = profil sous-utilisé. La projection &lt; 30j signale un risque de démission imminent.
        </p>
      </div>
    </div>
  );
}
